// ============================================================================
//  Servicio del Dashboard global (vista ejecutiva de toda la operación CX)
// ----------------------------------------------------------------------------
//  Arquitectura: `cargarDatos()` trae las fuentes UNA vez y `derivar()` calcula
//  el workspace para un juego de filtros. Cambiar un filtro NO vuelve a pedir
//  datos al servidor, salvo el filtro de cuenta cuando se necesita el desglose
//  por sección de esa cuenta (`cargarCategorias`).
//
//  FUENTES
//     GET /cuentas                -> universo de cuentas activas (denominador)
//     GET /ejecuciones            -> SIN filtros devuelve TODAS las ejecuciones
//     GET /incidencias            -> portal de seguimiento (estatus / área)
//     GET /contactos/resumen      -> KPIs del directorio
//     GET /dashboard_global       -> resumen y serie anual autoritativos
//     GET /dashboard/categorias   -> score por sección y año (?anio=, ?idCuenta=)
//
//  La lista /ejecuciones devuelve las claves en MINÚSCULAS (idejecucion,
//  scorepct, …), a diferencia del detalle /ejecuciones/:id que las devuelve en
//  camelCase. Se lee con pick() para tolerar ambas.
// ============================================================================

import { apiGet } from "./api";
import { sesion, conAuth } from "./cx.service";

// Umbrales del semáforo, alineados con el scoring de ORDS.
const COLOR_SEMAFORO = {
  VERDE: "#43B234",
  AMARILLO: "#FFC24B",
  ROJO: "#FF5D73",
  SIN_DATO: "#8090B0",
};

const COLOR_ESTATUS_INCIDENCIA = {
  Abierto: "#1A5CC9",
  "En proceso": "#FFC24B",
  "Stand by": "#A0B8D8",
  Concluido: "#43B234",
  Declinado: "#FF5D73",
};

const PALETA_AREAS = [
  "#003CA5",
  "#1A5CC9",
  "#43B234",
  "#5CC94E",
  "#FFC24B",
  "#FF5D73",
  "#A0B8D8",
  "#002A7A",
];

// Estado inicial: comparativo de todos los años, sin acotar por cuenta.
export const FILTROS_DEFAULT = {
  modo: "comparativo", // 'comparativo' | 'anio'
  anio: null, // año activo cuando modo === 'anio'
  // Años a comparar cuando modo === 'comparativo'. Vacío = todos los que haya.
  aniosComparar: [],
  idCuenta: null,
  area: null,
  estado: "todos", // 'todos' | 'VERDE' | 'AMARILLO' | 'ROJO'
  evaluacion: "todas", // 'todas' | 'evaluadas' | 'pendientes'
};

// Directorio completo de contactos: solo lo necesita el panel de detalle, así
// que se pide una vez y se reutiliza en las siguientes aperturas.
let cacheContactos = null;

// Lee la primera clave presente (tolera minúsculas vs camelCase de ORDS).
function pick(obj, ...claves) {
  for (const c of claves) {
    if (obj && obj[c] != null) return obj[c];
  }
  return null;
}

function promedio(valores) {
  const v = valores.filter((x) => x != null && !Number.isNaN(x));
  if (!v.length) return null;
  return v.reduce((a, b) => a + b, 0) / v.length;
}

function redondea(valor, decimales = 1) {
  if (valor == null) return null;
  const f = Math.pow(10, decimales);
  return Math.round(valor * f) / f;
}

// Año de la ejecución. `periodo` viene como "2024" o "2026-07"; si falta se
// intenta con la fecha (la lista la manda como texto, p. ej. "Julio 2026").
function anioDe(ejec) {
  const periodo = String(pick(ejec, "periodo", "PERIODO") || "");
  const mPeriodo = periodo.match(/(20\d{2})/);
  if (mPeriodo) return Number(mPeriodo[1]);
  const fecha = String(pick(ejec, "fechaaplicacion", "fechaAplicacion") || "");
  const mFecha = fecha.match(/(20\d{2})/);
  return mFecha ? Number(mFecha[1]) : null;
}

// Normaliza una fila de /ejecuciones al shape que consumen los cálculos.
function hydrateEjecucion(e) {
  return {
    idEjecucion: pick(e, "idejecucion", "idEjecucion"),
    idCuenta: pick(e, "idcuenta", "idCuenta"),
    cliente: pick(e, "cliente", "CLIENTE") || "—",
    contacto: pick(e, "nombrecontacto", "nombreContacto"),
    scorePct: Number(pick(e, "scorepct", "scorePct") ?? 0),
    nps: pick(e, "nps", "NPS"),
    ces: pick(e, "ces", "CES"),
    csat: pick(e, "csat", "CSAT"),
    semaforo: String(pick(e, "semaforo", "SEMAFORO") || "SIN_DATO").toUpperCase(),
    periodo: pick(e, "periodo", "PERIODO"),
    fechaAplicacion: pick(e, "fechaaplicacion", "fechaAplicacion"),
    anio: anioDe(e),
  };
}

// ---------------------------------------------------------------------------
//  Derivaciones
// ---------------------------------------------------------------------------

// NPS real (no promedio de calificaciones): %promotores(9-10) − %detractores(0-6).
// El campo `nps` de cada ejecución es una calificación 0-10 de un contacto, así
// que la métrica NPS se construye sobre esa distribución.
function calcularNps(ejecuciones) {
  const votos = ejecuciones.map((e) => e.nps).filter((v) => v != null);
  if (!votos.length) return { valor: null, muestra: 0 };
  const promotores = votos.filter((v) => v >= 9).length;
  const detractores = votos.filter((v) => v <= 6).length;
  return {
    valor: Math.round(((promotores - detractores) / votos.length) * 100),
    muestra: votos.length,
  };
}

// Última ejecución de cada cuenta = su estado vigente. Se desempata por año e
// idEjecucion para no depender del orden que mande el servidor.
function ultimaPorCuenta(ejecuciones) {
  const mapa = new Map();
  ejecuciones.forEach((e) => {
    const actual = mapa.get(e.idCuenta);
    if (
      !actual ||
      (e.anio || 0) > (actual.anio || 0) ||
      ((e.anio || 0) === (actual.anio || 0) && e.idEjecucion > actual.idEjecucion)
    ) {
      mapa.set(e.idCuenta, e);
    }
  });
  return Array.from(mapa.values());
}

function distribucionSemaforo(vigentes) {
  const total = vigentes.length || 1;
  return [
    { key: "VERDE", label: "Favorable", color: COLOR_SEMAFORO.VERDE },
    { key: "AMARILLO", label: "En seguimiento", color: COLOR_SEMAFORO.AMARILLO },
    { key: "ROJO", label: "Crítico", color: COLOR_SEMAFORO.ROJO },
  ].map((bucket) => {
    const value = vigentes.filter((e) => e.semaforo === bucket.key).length;
    return { ...bucket, value, pct: Math.round((value / total) * 100) };
  });
}

function rankingCuentas(ejecuciones, vigentes) {
  const porCuenta = new Map();
  ejecuciones.forEach((e) => {
    if (!porCuenta.has(e.idCuenta)) porCuenta.set(e.idCuenta, []);
    porCuenta.get(e.idCuenta).push(e);
  });

  return vigentes
    .map((v) => {
      const historial = (porCuenta.get(v.idCuenta) || [])
        .slice()
        .sort((a, b) => (a.anio || 0) - (b.anio || 0) || a.idEjecucion - b.idEjecucion);
      const previa = historial.length > 1 ? historial[historial.length - 2] : null;
      return {
        idCuenta: v.idCuenta,
        cliente: v.cliente,
        score: redondea(v.scorePct),
        semaforo: v.semaforo,
        color: COLOR_SEMAFORO[v.semaforo] || COLOR_SEMAFORO.SIN_DATO,
        evaluaciones: historial.length,
        ultimaEval: v.fechaAplicacion || (v.anio ? String(v.anio) : "—"),
        delta: previa ? redondea(v.scorePct - previa.scorePct) : null,
      };
    })
    .sort((a, b) => b.score - a.score);
}

// Serie anual: score, NPS, CES, CSAT, volumen y cobertura por año.
// Granularidad ANUAL — el campo `periodo` no trae mes salvo casos aislados.
// Es la única lectura temporal que NO se ve afectada por esa carencia, porque
// el resumen ejecutivo es anual por definición.
function evolucionAnual(ejecuciones, cuentasObjetivo, aniosForzados) {
  const anios =
    aniosForzados ||
    Array.from(new Set(ejecuciones.map((e) => e.anio).filter(Boolean))).sort();

  return anios.map((anio) => {
    const delAnio = ejecuciones.filter((e) => e.anio === anio);
    const cuentas = new Set(delAnio.map((e) => e.idCuenta)).size;
    const csatValores = delAnio.map((e) => e.csat).filter((v) => v != null);
    return {
      anio,
      score: redondea(promedio(delAnio.map((e) => e.scorePct))),
      nps: calcularNps(delAnio).valor,
      ces: redondea(promedio(delAnio.map((e) => e.ces).filter((v) => v != null))),
      csat: redondea(promedio(csatValores)),
      csatMuestra: csatValores.length,
      encuestas: delAnio.length,
      cuentas,
      // Cobertura DEL AÑO: cuentas evaluadas ese año sobre el total activo.
      // Distinta de la cobertura del bloque `resumen`, que cuenta las cuentas
      // con alguna evaluación en cualquier año.
      cobertura: cuentasObjetivo
        ? Math.round((cuentas / cuentasObjetivo) * 100)
        : 0,
    };
  });
}

// Fusiona la serie calculada en cliente con la de /dashboard_global. ORDS es
// autoritativo en score, CES, CSAT, cobertura y volúmenes; del cálculo local se
// conserva el NPS y la muestra de CSAT, que el servicio no expone.
//
// NOTA sobre `nps`: /dashboard_global devuelve el PROMEDIO de la calificación
// 0-10 (8.5 / 8.7 / 8.5), no un Net Promoter Score. Un NPS va de -100 a 100 y
// se calcula %promotores(9-10) − %detractores(0-6), lo que da 46 / 64 / 56 con
// estos mismos datos. Se conserva el cálculo local y el promedio del servicio
// se guarda aparte como `npsPromedio` para no perder el dato.
function fusionarEvolucion(serieLocal, evolutionOrds) {
  if (!Array.isArray(evolutionOrds) || !evolutionOrds.length) {
    return serieLocal.map((p) => ({ ...p, npsPromedio: null, fuente: "cliente" }));
  }

  return serieLocal.map((p) => {
    const remoto = evolutionOrds.find((e) => Number(e.year) === p.anio);
    if (!remoto) return { ...p, npsPromedio: null, fuente: "cliente" };
    return {
      ...p,
      score: remoto.calificacion ?? p.score,
      ces: remoto.ces ?? p.ces,
      csat: remoto.csat ?? p.csat,
      encuestas: remoto.encuestas ?? p.encuestas,
      cuentas: remoto.cuentas ?? p.cuentas,
      cobertura:
        remoto.cobertura != null ? Math.round(remoto.cobertura) : p.cobertura,
      npsPromedio: remoto.nps ?? null,
      fuente: "ords",
    };
  });
}

// Matriz sección × año a partir de GET /dashboard/categorias.
//
// SALVAGUARDA: el servicio devuelve los scores de 2024 en una escala distinta
// (22-27, todos ROJO) frente a 2025-2026 (82-90, todos VERDE). Es el efecto del
// peso de sección sin normalizar — mismo problema que corrige
// database/fix/fix_peso_seccion_scoring_v2.sql. Graficarlos juntos inventa una
// caída y una recuperación que no ocurrieron, así que los años fuera de escala
// se marcan y se excluyen de la comparación. La detección es automática: cuando
// el backend corrija el scoring, la advertencia desaparece sola.
function construirCategorias(items, filtros = {}) {
  if (!Array.isArray(items) || !items.length) {
    return { disponible: false, anios: [], secciones: [], matriz: [] };
  }

  const anios = Array.from(new Set(items.map((i) => i.anio))).sort();
  const todasSecciones = Array.from(new Set(items.map((i) => i.seccion))).sort();

  // Un año se considera fuera de escala si NINGUNA de sus secciones supera 50
  // mientras otro año sí lo hace: las escalas no son comparables entre sí.
  const maxPorAnio = new Map(
    anios.map((a) => [
      a,
      Math.max(...items.filter((i) => i.anio === a).map((i) => i.score || 0)),
    ]),
  );
  const hayEscalaAlta = Array.from(maxPorAnio.values()).some((m) => m >= 50);
  const aniosFueraDeEscala = hayEscalaAlta
    ? anios.filter((a) => maxPorAnio.get(a) < 50)
    : [];

  let aniosComparables = anios.filter((a) => !aniosFueraDeEscala.includes(a));
  if (filtros.aniosActivos && filtros.aniosActivos.length) {
    aniosComparables = aniosComparables.filter((a) =>
      filtros.aniosActivos.includes(a),
    );
  }

  const secciones = filtros.area
    ? todasSecciones.filter((s) => s === filtros.area)
    : todasSecciones;

  const matriz = secciones
    .map((seccion) => ({
      seccion,
      valores: aniosComparables.map((anio) => {
        const it = items.find((i) => i.anio === anio && i.seccion === seccion);
        return {
          anio,
          score: it ? it.score : null,
          semaforo: it ? it.semaforo : null,
          ejecuciones: it ? it.ejecuciones : 0,
        };
      }),
    }))
    // Una sección cuyo único dato estaba en un año excluido queda totalmente
    // vacía; se omite en lugar de dibujar una columna en blanco.
    .filter((f) => f.valores.some((v) => v.score != null));

  const aniosIncompletos = aniosComparables.filter(
    (a) => items.filter((i) => i.anio === a).length < todasSecciones.length,
  );

  return {
    disponible: matriz.length > 0 && aniosComparables.length > 0,
    anios: aniosComparables,
    aniosFueraDeEscala,
    aniosIncompletos,
    secciones: todasSecciones,
    matriz,
  };
}

// Resumen ejecutivo anual: tarjetas de titular + lecturas en texto derivadas
// de la serie. Todo se calcula sobre `serie`; no requiere llamadas extra.
function construirResumenAnual(serie, etiqueta) {
  if (serie.length < 2) return { highlights: [], lecturas: [], disponible: false };

  const primero = serie[0];
  const ultimo = serie[serie.length - 1];
  const porScore = serie.slice().sort((a, b) => (b.score || 0) - (a.score || 0));
  const porCobertura = serie.slice().sort((a, b) => b.cobertura - a.cobertura);
  const porCuentas = serie.slice().sort((a, b) => b.cuentas - a.cuentas);
  const mejorScore = porScore[0];
  const mayorCobertura = porCobertura[0];
  const masCuentas = porCuentas[0];
  const totalEncuestas = serie.reduce((s, p) => s + p.encuestas, 0);

  const deltaScore = redondea((ultimo.score || 0) - (primero.score || 0));
  const deltaNps =
    ultimo.nps != null && primero.nps != null ? ultimo.nps - primero.nps : null;
  const signo = (v) => (v >= 0 ? "+" : "");

  const highlights = [
    {
      label: "Evolución global",
      value: `${signo(deltaScore)}${deltaScore} pts vs ${primero.anio}`,
      tone: deltaScore >= 0 ? "success" : "danger",
    },
    {
      label: "Mejor resultado anual",
      value: `${mejorScore.anio} · ${mejorScore.score}%`,
      tone: "primary",
    },
    {
      label: "Mayor cobertura",
      value: `${mayorCobertura.anio} · ${mayorCobertura.cobertura}%`,
      tone: "warning",
    },
    {
      label: "Volumen acumulado ESAC",
      value: `${totalEncuestas} encuestas aplicadas`,
      tone: "neutral",
    },
    {
      label: "Crecimiento NPS",
      value:
        deltaNps == null
          ? "Sin dato comparable"
          : `${signo(deltaNps)}${deltaNps} pts de ${primero.anio} a ${ultimo.anio}`,
      tone: deltaNps == null ? "neutral" : deltaNps >= 0 ? "success" : "danger",
    },
    {
      label: "Cuentas evaluadas",
      value: `${masCuentas.cuentas} cuentas en el mejor año`,
      tone: "primary",
    },
  ];

  const sujeto = etiqueta || "ONEST";
  const lecturas = [];
  lecturas.push(
    deltaScore >= 0
      ? `${sujeto} pasa de ${primero.score}% en ${primero.anio} a ${ultimo.score}% en ${ultimo.anio} (${signo(deltaScore)}${deltaScore} puntos).`
      : `${sujeto} retrocede de ${primero.score}% en ${primero.anio} a ${ultimo.score}% en ${ultimo.anio} (${deltaScore} puntos).`,
  );
  if (deltaNps != null) {
    lecturas.push(
      `El NPS ${deltaNps >= 0 ? "aumentó" : "disminuyó"} ${Math.abs(deltaNps)} puntos en el periodo (${primero.nps} → ${ultimo.nps}).`,
    );
  }
  lecturas.push(
    `${mejorScore.anio} mantiene el mejor resultado global (${mejorScore.score}%) con ${mejorScore.cuentas} cuentas evaluadas.`,
  );

  // Advertencia clave: un score alto con cobertura baja se apoya en una
  // muestra menor. Solo se emite cuando el mejor año NO es el de más cobertura.
  if (mejorScore.anio !== mayorCobertura.anio) {
    lecturas.push(
      `Atención: el mejor score (${mejorScore.anio}, ${mejorScore.score}%) no coincide con el año de mayor cobertura (${mayorCobertura.anio}, ${mayorCobertura.cobertura}%). El resultado se apoya en una muestra más pequeña.`,
    );
  }

  const sinCsat = serie.filter((p) => p.csat == null);
  if (sinCsat.length) {
    lecturas.push(
      `Sin dato de CSAT en ${sinCsat.map((p) => p.anio).join(", ")}: la lectura de satisfacción no es comparable en todo el periodo.`,
    );
  }

  lecturas.push(
    `Volumen acumulado del periodo: ${totalEncuestas} encuestas ESAC sobre ${serie.length} años.`,
  );

  return { highlights, lecturas, disponible: true };
}

// Tabla comparativa de cuentas: una columna por año activo, más variación,
// tendencia y última evaluación. Es el equivalente del comparativo por cuenta.
function comparativoCuentas(ejecuciones, aniosActivos, cuentasCatalogo, filtros) {
  const porCuenta = new Map();
  ejecuciones.forEach((e) => {
    if (!porCuenta.has(e.idCuenta)) {
      porCuenta.set(e.idCuenta, { idCuenta: e.idCuenta, cliente: e.cliente, ejecs: [] });
    }
    porCuenta.get(e.idCuenta).ejecs.push(e);
  });

  const filas = Array.from(porCuenta.values()).map((c) => {
    // Score de la cuenta en cada año activo: promedio de sus ejecuciones.
    const porAnio = aniosActivos.map((anio) => {
      const delAnio = c.ejecs.filter((e) => e.anio === anio);
      return {
        anio,
        score: redondea(promedio(delAnio.map((e) => e.scorePct))),
        encuestas: delAnio.length,
      };
    });

    const conDato = porAnio.filter((p) => p.score != null);
    const primero = conDato[0] || null;
    const ultimo = conDato[conDato.length - 1] || null;
    const vigente = ultimaPorCuenta(c.ejecs)[0];

    return {
      idCuenta: c.idCuenta,
      cliente: c.cliente,
      porAnio,
      semaforo: vigente ? vigente.semaforo : "SIN_DATO",
      color: COLOR_SEMAFORO[vigente?.semaforo] || COLOR_SEMAFORO.SIN_DATO,
      scoreVigente: vigente ? redondea(vigente.scorePct) : null,
      variacion:
        conDato.length > 1 ? redondea(ultimo.score - primero.score) : null,
      encuestas: c.ejecs.length,
      ultimaEval: vigente ? vigente.fechaAplicacion || String(vigente.anio) : "—",
      evaluada: true,
    };
  });

  // Cuentas activas sin ninguna ejecución en el periodo filtrado.
  const evaluadas = new Set(filas.map((f) => String(f.idCuenta)));
  const pendientes = cuentasCatalogo
    .filter((c) => !evaluadas.has(String(pick(c, "idcuenta", "idCuenta"))))
    .map((c) => ({
      idCuenta: pick(c, "idcuenta", "idCuenta"),
      cliente: pick(c, "nombre", "NOMBRE") || "—",
      porAnio: aniosActivos.map((anio) => ({ anio, score: null, encuestas: 0 })),
      semaforo: "SIN_DATO",
      color: COLOR_SEMAFORO.SIN_DATO,
      scoreVigente: null,
      variacion: null,
      encuestas: 0,
      ultimaEval: "—",
      evaluada: false,
    }));

  let resultado;
  if (filtros.evaluacion === "pendientes") resultado = pendientes;
  else if (filtros.evaluacion === "evaluadas") resultado = filas;
  else resultado = [...filas, ...pendientes];

  return resultado.sort((a, b) => {
    if (a.scoreVigente == null && b.scoreVigente == null)
      return a.cliente.localeCompare(b.cliente, "es");
    if (a.scoreVigente == null) return 1;
    if (b.scoreVigente == null) return -1;
    return b.scoreVigente - a.scoreVigente;
  });
}

// Comparación de una cuenta contra el promedio ONEST del mismo periodo.
function cuentaVsOnest(ejecCuenta, ejecTodas) {
  if (!ejecCuenta.length) return null;
  const met = (arr, campo) =>
    redondea(promedio(arr.map((e) => e[campo]).filter((v) => v != null)));

  return [
    {
      metrica: "Score %",
      cuenta: met(ejecCuenta, "scorePct"),
      onest: met(ejecTodas, "scorePct"),
    },
    {
      metrica: "NPS",
      cuenta: calcularNps(ejecCuenta).valor,
      onest: calcularNps(ejecTodas).valor,
    },
    { metrica: "CES", cuenta: met(ejecCuenta, "ces"), onest: met(ejecTodas, "ces") },
    {
      metrica: "CSAT",
      cuenta: met(ejecCuenta, "csat"),
      onest: met(ejecTodas, "csat"),
    },
  ];
}

function agrupaIncidencias(incidencias, campo, colores) {
  const conteo = new Map();
  incidencias.forEach((i) => {
    const clave = i[campo] || "Sin clasificar";
    conteo.set(clave, (conteo.get(clave) || 0) + 1);
  });
  return Array.from(conteo.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, value], idx) => ({
      name,
      value,
      color: colores
        ? colores[name] || PALETA_AREAS[idx % PALETA_AREAS.length]
        : PALETA_AREAS[idx % PALETA_AREAS.length],
    }));
}

// ---------------------------------------------------------------------------
//  API pública
// ---------------------------------------------------------------------------

export const dashboardService = {
  // Carga las fuentes en paralelo. Solo /ejecuciones es bloqueante; el resto
  // degrada de forma parcial para que el tablero siga siendo utilizable.
  async cargarDatos() {
    const s = await sesion().catch(() => ({ idColaborador: null, rol: "" }));
    const [
      cuentasRes,
      ejecucionesRes,
      incidenciasRes,
      contactosRes,
      globalRes,
      categoriasRes,
    ] = await Promise.allSettled([
      apiGet(conAuth("/cuentas", s)),
      apiGet(conAuth("/ejecuciones", s)),
      apiGet(conAuth("/incidencias", s)),
      apiGet("/contactos/resumen"),
      apiGet(conAuth("/dashboard_global", s)),
      apiGet(conAuth("/dashboard/categorias", s)),
    ]);

    if (ejecucionesRes.status !== "fulfilled") {
      throw new Error("No fue posible leer las ejecuciones del servicio ORDS.");
    }

    const okOrds = (res) =>
      res.status === "fulfilled" &&
      res.value &&
      Number(res.value.ERROR_ID) === 200;

    return {
      cuentas:
        cuentasRes.status === "fulfilled" ? cuentasRes.value?.items || [] : [],
      ejecuciones: (ejecucionesRes.value?.items || []).map(hydrateEjecucion),
      incidencias:
        incidenciasRes.status === "fulfilled"
          ? incidenciasRes.value?.INCIDENCIAS || []
          : [],
      incidenciasOk: incidenciasRes.status === "fulfilled",
      contactos:
        contactosRes.status === "fulfilled" ? contactosRes.value || null : null,
      global: okOrds(globalRes) ? globalRes.value : null,
      categoriasItems: okOrds(categoriasRes) ? categoriasRes.value.items || [] : [],
    };
  },

  // Desglose por sección de UNA cuenta. Se pide aparte porque solo hace falta
  // cuando el usuario acota el tablero a una cuenta concreta.
  async cargarCategoriasCuenta(idCuenta) {
    if (!idCuenta) return null;
    const s = await sesion().catch(() => ({ idColaborador: null, rol: "" }));
    const data = await apiGet(
      conAuth("/dashboard/categorias?idCuenta=" + encodeURIComponent(idCuenta), s),
    );
    if (!data || Number(data.ERROR_ID) !== 200) return null;
    return data.items || [];
  },

  // Datos del panel de detalle de una cuenta. Se cargan bajo demanda al abrir
  // el panel, no en el arranque del tablero.
  //   · categorías de la cuenta   -> pestaña Áreas
  //   · contactos del directorio  -> pestaña Resumen (se cachea: 36 KB)
  //   · detalle de la última ejecución -> comentarios por sección (Hallazgos)
  async cargarDetalleCuenta(idCuenta, idUltimaEjecucion) {
    const [catRes, contRes, ejecRes] = await Promise.allSettled([
      this.cargarCategoriasCuenta(idCuenta),
      cacheContactos
        ? Promise.resolve(cacheContactos)
        : apiGet("/contactos").then((d) => {
            cacheContactos = d?.items || [];
            return cacheContactos;
          }),
      idUltimaEjecucion
        ? apiGet("/ejecuciones/" + encodeURIComponent(idUltimaEjecucion))
        : Promise.resolve(null),
    ]);

    const contactos = contRes.status === "fulfilled" ? contRes.value || [] : [];
    const detalle = ejecRes.status === "fulfilled" ? ejecRes.value : null;

    return {
      categoriasItems: catRes.status === "fulfilled" ? catRes.value || [] : [],
      contactos: contactos.filter(
        (c) => String(pick(c, "idcuenta", "idCuenta")) === String(idCuenta),
      ),
      // El detalle trae los comentarios abiertos que capturó el cliente. Son
      // los hallazgos reales, sin clasificar: no existe taxonomía en el modelo.
      comentarios: detalle?.comentariosSeccion || [],
      respuestasAbiertas: (detalle?.respuestas || []).filter(
        (r) => r.comentario && String(r.comentario).trim(),
      ),
    };
  },

  // Expone la construcción de la matriz sección × año para que el panel de
  // detalle reutilice las mismas salvaguardas de escala que el tablero.
  categoriasDeItems(items, filtros = {}) {
    return construirCategorias(items, filtros);
  },

  // Calcula el workspace completo para un juego de filtros. Puro cálculo:
  // no hace peticiones.
  derivar(raw, filtros = FILTROS_DEFAULT) {
    const aniosDisponibles = Array.from(
      new Set(raw.ejecuciones.map((e) => e.anio).filter(Boolean)),
    ).sort();

    // En modo comparativo se respeta la selección de años; si está vacía o
    // ninguno de los elegidos existe en los datos, se usan todos.
    let aniosActivos;
    if (filtros.modo === "anio" && filtros.anio) {
      aniosActivos = [Number(filtros.anio)];
    } else {
      const elegidos = (filtros.aniosComparar || [])
        .map(Number)
        .filter((a) => aniosDisponibles.includes(a))
        .sort((a, b) => a - b);
      aniosActivos = elegidos.length ? elegidos : aniosDisponibles;
    }

    const cuentaSel = filtros.idCuenta ? String(filtros.idCuenta) : null;

    // 1) Recorte por año y cuenta.
    let ejecuciones = raw.ejecuciones.filter((e) =>
      aniosActivos.includes(e.anio),
    );
    const ejecucionesPeriodo = ejecuciones; // sin acotar por cuenta: benchmark
    if (cuentaSel) {
      ejecuciones = ejecuciones.filter((e) => String(e.idCuenta) === cuentaSel);
    }

    // 2) Recorte por semáforo vigente de la cuenta.
    if (filtros.estado && filtros.estado !== "todos") {
      const permitidas = new Set(
        ultimaPorCuenta(ejecuciones)
          .filter((e) => e.semaforo === filtros.estado)
          .map((e) => String(e.idCuenta)),
      );
      ejecuciones = ejecuciones.filter((e) => permitidas.has(String(e.idCuenta)));
    }

    const vigentes = ultimaPorCuenta(ejecuciones);
    const cuentasEvaluadas = vigentes.length;
    const cuentasObjetivo = cuentaSel
      ? 1
      : raw.cuentas.length || cuentasEvaluadas;

    const nps = calcularNps(ejecuciones);
    const csatValores = ejecuciones.map((e) => e.csat).filter((v) => v != null);
    const cesValores = ejecuciones.map((e) => e.ces).filter((v) => v != null);
    const ranking = rankingCuentas(ejecuciones, vigentes);

    // 3) Serie anual. La fusión con /dashboard_global solo aplica a la vista
    //    global: sus cifras son de toda la operación, no de un recorte.
    const esGlobal =
      !cuentaSel &&
      (!filtros.estado || filtros.estado === "todos") &&
      aniosActivos.length === aniosDisponibles.length;

    const serieLocal = evolucionAnual(ejecuciones, cuentasObjetivo, aniosActivos);
    const evolucion = esGlobal
      ? fusionarEvolucion(serieLocal, raw.global?.evolution)
      : serieLocal.map((p) => ({ ...p, npsPromedio: null, fuente: "cliente" }));

    // Referencia ONEST para superponer cuando el tablero está acotado.
    const benchmark = cuentaSel
      ? evolucionAnual(
          raw.ejecuciones.filter((e) => aniosActivos.includes(e.anio)),
          raw.cuentas.length,
          aniosActivos,
        )
      : null;

    const nombreCuenta = cuentaSel
      ? vigentes[0]?.cliente ||
        pick(
          raw.cuentas.find(
            (c) => String(pick(c, "idcuenta", "idCuenta")) === cuentaSel,
          ) || {},
          "nombre",
        ) ||
        "Cuenta"
      : null;

    // 4) Incidencias: se recortan por cliente cuando hay cuenta seleccionada.
    const incidencias = nombreCuenta
      ? raw.incidencias.filter((i) => i.cliente === nombreCuenta)
      : raw.incidencias;

    return {
      filtros,
      aniosDisponibles,
      aniosActivos,
      nombreCuenta,
      esGlobal,

      resumen: {
        scoreGlobal: redondea(promedio(ejecuciones.map((e) => e.scorePct))),
        nps: nps.valor,
        npsMuestra: nps.muestra,
        ces: redondea(promedio(cesValores)),
        cesMuestra: cesValores.length,
        // CSAT llega nulo en buena parte de las ejecuciones: se expone la
        // muestra para que el número no se lea como cobertura total.
        csat: redondea(promedio(csatValores)),
        csatMuestra: csatValores.length,
        totalEjecuciones: ejecuciones.length,
        cuentasEvaluadas,
        cuentasObjetivo,
        cobertura: cuentasObjetivo
          ? Math.round((cuentasEvaluadas / cuentasObjetivo) * 100)
          : 0,
        cuentasEnRiesgo: vigentes.filter((e) => e.semaforo === "ROJO").length,
        cuentasEnSeguimiento: vigentes.filter((e) => e.semaforo === "AMARILLO")
          .length,
        totalContactos: raw.contactos?.totalContactos ?? null,
        decisionMakers: raw.contactos?.decisionMakers ?? null,
        promotores: raw.contactos?.promotores ?? null,
        warnings: raw.contactos?.warnings ?? null,
      },

      cobertura: [
        { name: "Evaluadas", value: cuentasEvaluadas, color: COLOR_SEMAFORO.VERDE },
        {
          name: "Sin evaluación",
          value: Math.max(0, cuentasObjetivo - cuentasEvaluadas),
          color: COLOR_SEMAFORO.SIN_DATO,
        },
      ],
      semaforo: distribucionSemaforo(vigentes),
      ranking,
      top: ranking.slice(0, 5),
      bottom: ranking.slice(-5).reverse(),

      evolucion,
      benchmark,
      resumenAnual: construirResumenAnual(evolucion, nombreCuenta),

      categorias: construirCategorias(
        raw.categoriasCuenta || raw.categoriasItems,
        { aniosActivos, area: filtros.area },
      ),

      comparativo: comparativoCuentas(
        ejecuciones,
        aniosActivos,
        cuentaSel ? [] : raw.cuentas,
        filtros,
      ),

      vsOnest: cuentaSel ? cuentaVsOnest(ejecuciones, ejecucionesPeriodo) : null,

      incidencias: {
        total: incidencias.length,
        disponible: raw.incidenciasOk,
        porEstatus: agrupaIncidencias(
          incidencias,
          "estatus",
          COLOR_ESTATUS_INCIDENCIA,
        ),
        porArea: agrupaIncidencias(incidencias, "areaResponsable").slice(0, 8),
      },
    };
  },
};

export default dashboardService;
